import { useApolloClient } from '@vue/apollo-composable';

import { SESSION_STORAGE_KEYS } from '~/core';
import { useBasketPayload, useBookingStore } from '~/domains/booking';
import {
  BasketDocument,
  CreateBasketDocument,
  UpdateBasketDocument,
  type Basket,
  type BasketQuery,
  type CreateBasketMutation,
  type UpdateBasketMutation,
} from '~/domains/graphql';
import { useUserStore } from '~/domains/user';

let initialized = false;

export function useBookingBasket() {
  const bookingStore = useBookingStore();
  const userStore = useUserStore();
  const route = useRoute();
  const basketPayload = useBasketPayload();
  const { client } = useApolloClient();
  const hasIdentificationCookies = useHasIdentificationCookies();

  const updateBasket = async () => {
    bookingStore.loading = true;

    hydrateBasketIDToRooms(bookingStore.currentBasket as Basket);

    const result = await client.mutate<UpdateBasketMutation>({
      mutation: UpdateBasketDocument,
      variables: basketPayload.value,
      fetchPolicy: 'network-only',
    });

    const basket = result.data?.updateBasket;

    if (basket) {
      const basketId = basket.id;

      bookingStore.basketId = basketId;
      bookingStore.currentBasket = basket as Basket;
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.BASKET_ID, basketId);
    }

    bookingStore.loading = false;
  };

  const createBasket = async () => {
    bookingStore.loading = true;

    const result = await client.mutate<CreateBasketMutation>({
      mutation: CreateBasketDocument,
      variables: basketPayload.value,
      fetchPolicy: 'no-cache',
    });

    const basket = result.data?.createBasket;

    if (basket) {
      const basketId = basket.id;

      bookingStore.basketId = basketId;
      bookingStore.currentBasket = basket as Basket;
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.BASKET_ID, basketId);

      hydrateBasketIDToRooms(basket as Basket);
    }

    bookingStore.loading = false;
  };

  const getBasket = async (basketId: string) => {
    bookingStore.loading = true;

    const result = await client.query<BasketQuery>({
      query: BasketDocument,
      variables: { basketId },
    });

    const basket = result.data?.basket;

    if (basket) {
      bookingStore.basketId = basketId;
      bookingStore.currentBasket = basket as Basket;

      hydrateBasketIDToRooms(basket as Basket);
    }

    bookingStore.loading = false;
  };

  function hydrateBasketIDToRooms(basket: Basket) {
    if (!basket) return;
    if (!Array.isArray(basket.rooms)) return;

    basket.rooms.forEach(room => {
      if (!room || !room.productCode || !room.id) {
        return;
      }

      const storeRoomIndex = bookingStore.rooms.findIndex(
        roomInStore =>
          roomInStore.productCode === room.productCode &&
          !roomInStore.basketRoomId,
      );

      if (storeRoomIndex === -1) {
        return;
      }

      bookingStore.rooms[storeRoomIndex].basketRoomId = room.id;
    });
  }

  async function init() {
    if (process.server) return;
    if (initialized) return;

    while (!hasIdentificationCookies.value || !userStore.hasLoadedUser) {
      await new Promise(resolve => nextTick(() => setTimeout(resolve, 100)));
    }

    // Hook Initialisation
    if (bookingStore.basketId) return;

    const previousBasketId = window.sessionStorage.getItem(
      SESSION_STORAGE_KEYS.BASKET_ID,
    );

    if (
      ['details', 'payment'].find(routeName =>
        (route.name?.toString() ?? '').includes(routeName),
      )
    ) {
      if (previousBasketId) {
        await getBasket(previousBasketId);
      } else {
        await createBasket();
      }
    }

    if (
      (route.name?.toString() ?? '').includes('payment') &&
      bookingStore.beneficiary
    ) {
      await updateBasket();
    }

    initialized = true;
  }

  return {
    createBasket,
    updateBasket,
    init,
  };
}
